import { type DefaultProps, RangeSliderV2 } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { CUSTOMER_SERVICE_PHONE_NUMBER, CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY } from '@egw/constants'
import cx from 'classnames'
import { type ChangeEvent, type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import { getEquitySliderLabel } from '../../utils/equity/get-equity-slider-label'
import { ExperienceName } from '../../constants/api/experience-name'

export interface SliderData {
  type: string
  maxAllocationPercent: number
  currentAllocationPercent?: number
  recommendedRange: {
    recommendedPercent: number
    allowedMinPercent: number
    allowedMaxPercent: number
  }
}

export interface SectorSlidersProps extends DefaultProps {
  onChange: (index: number, event: ChangeEvent<HTMLInputElement>) => void
  sliderData: SliderData[]
  isShowCurrentValue?: boolean
  experience?: string
}

export const SectorSliders: FC<SectorSlidersProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  sliderData,
  onChange,
  isShowCurrentValue = false,
  experience = ExperienceName.BBQ,
}) => {
  const { t } = useTranslation('features-enrollment')

  const isFirstAccount = experience === ExperienceName.BBQ

  // Component base class names. Can be overriden by className property.
  const baseClass = cx('w-full')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div className="w-full flex flex-col">
        <div className="flex flex-col gap-8">
          {sliderData.map((slider, index) => {
            const { recommendedPercent, allowedMinPercent, allowedMaxPercent } = slider.recommendedRange
            const { currentAllocationPercent } = slider
            const isNotInRecommendedRange =
              (currentAllocationPercent ?? recommendedPercent) < allowedMinPercent ||
              (currentAllocationPercent ?? recommendedPercent) > allowedMaxPercent
            // format label
            const label = getEquitySliderLabel(slider.type)
            return (
              <div key={slider.type}>
                <RangeSliderV2
                  fillFrom={allowedMinPercent}
                  fillTo={allowedMaxPercent}
                  max={slider.maxAllocationPercent}
                  label={label}
                  onChange={(event) => {
                    onChange(index, event)
                  }}
                  value={currentAllocationPercent ?? recommendedPercent}
                  recommendedValue={recommendedPercent}
                  showRecommededArrow
                  showOriginalArrow={isShowCurrentValue}
                  id={`range-slider-${index}`}
                  testId={`range-slider-${index}`}
                />
                {isNotInRecommendedRange ? (
                  <div
                    className="p-sm bg-gray-100 -mt-5 lg:-mt-md xl:-mt-md relative z-[1]"
                    data-testid={`slider-${index}-not-recommended`}
                  >
                    {/* below text not in translation because it is being used in enrollment */}
                    <p className="typo-primary-sm-regular mt-sm">
                      {isFirstAccount
                        ? t('investments.equity.outside_range')
                        : t('investments.equity.outside_range_teriyaki')}
                    </p>
                    <p className="typo-primary-sm-regular mt-xs">
                      {t('common.call')}&nbsp;
                      <a
                        href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                        className="typo-primary-sm-semibold  text-text-action"
                      >
                        {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                      </a>
                    </p>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
